import { useState } from 'react'
import type { FieldOffice } from '../../pages/admin/teamStore'

const FIELD = 'min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small'
const LABEL = 'mb-1 block text-caption font-bold text-ink-muted'

export type NewTeamValues = {
  name: string
  openDate: string
  closeDate?: string
  fieldOfficeId?: string
}

/**
 * docs/product/scheduling-model.md: every team carries an open date and an
 * optional close date, so a temporary team for a detail or an operation is
 * created with its end already known and drops out of the pickers when it
 * closes. A team starts with no supervisor; one is given when an account is
 * made to lead it. The field office is chosen only when there is more than
 * one.
 */
export default function TeamForm({
  today,
  fieldOffices,
  saving,
  error,
  onSave,
  onCancel,
}: {
  today: string
  fieldOffices: FieldOffice[]
  saving: boolean
  error: string | null
  onSave: (values: NewTeamValues) => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [openDate, setOpenDate] = useState(today)
  const [closeDate, setCloseDate] = useState('')
  const [fieldOfficeId, setFieldOfficeId] = useState(fieldOffices[0]?.id ?? '')

  const chooseOffice = fieldOffices.length > 1
  const canSave = name.trim() !== '' && openDate !== '' && (closeDate === '' || closeDate >= openDate)

  return (
    <form
      className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3"
      aria-label="Create team"
      onSubmit={(event) => {
        event.preventDefault()
        if (!canSave || saving) return
        onSave({
          name: name.trim(),
          openDate,
          closeDate: closeDate || undefined,
          fieldOfficeId: chooseOffice ? fieldOfficeId : undefined,
        })
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className={chooseOffice ? '' : 'sm:col-span-1'}>
          <label htmlFor="team-name" className={LABEL}>
            Team name
          </label>
          <input id="team-name" type="text" maxLength={100} value={name} onChange={(event) => setName(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="team-open-date" className={LABEL}>
            Opens
          </label>
          <input id="team-open-date" type="date" value={openDate} onChange={(event) => setOpenDate(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="team-close-date" className={LABEL}>
            Closes (optional)
          </label>
          <input
            id="team-close-date"
            type="date"
            min={openDate || undefined}
            aria-describedby="team-close-hint"
            value={closeDate}
            onChange={(event) => setCloseDate(event.target.value)}
            className={FIELD}
          />
          <p id="team-close-hint" className="mt-1 text-caption text-ink-subtle">
            Set for a temporary team; it drops out of the pickers when it closes.
          </p>
        </div>
        {chooseOffice && (
          <div>
            <label htmlFor="team-field-office" className={LABEL}>
              Field office
            </label>
            <select id="team-field-office" value={fieldOfficeId} onChange={(event) => setFieldOfficeId(event.target.value)} className={FIELD}>
              {fieldOffices.map((office) => (
                <option key={office.id} value={office.id}>
                  {office.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && (
        <p role="alert" className="text-caption font-semibold text-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSave || saving}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
        >
          {saving ? 'Creating…' : 'Create team'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
